// Engagement (28 Oct 2026): ECONIC's waitlist, points, streaks and leaderboards, served by `/api/econic/engagement` (the only ECONIC routes an
// account on the waitlist reaches). Files go to the Knowledge Bank's own encrypted parts (5 MiB raw PUTs, the bearer from the session), then
// its scanning, screening and the department's review; the person never reads them back, only what became of them.
import { useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, MAX_UPLOAD_BYTES, useApi, useAuth, type AuthUser } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { admissionReadyStanding, engagementPollInterval, rememberedAccessState } from "./admission";
import type { Unit } from "./units";

export type Period = "week" | "month" | "all";
/** A unit of the Ministry as engagement names it; `parent` is the sector a department belongs to (null at the top). */
export interface Dept { key: string; name: string; nameAr?: string | null; canContribute?: boolean; kind?: string; parent?: { key: string; name: string; nameAr?: string | null } | null }

export interface EngagementMe {
  userId: string;
  state: "waitlist" | "admitted";
  position: number | null;
  waitlistTotal: number;
  joinedAt: string | null;
  admittedAt: string | null;
  department: Dept | null;
  departmentClaimed: boolean;
  /** False until the person finished or skipped the welcome journey (an account from before the waitlist is always true). */
  onboarded?: boolean;
  name?: string;
  nameAr?: string | null;
  /** The unit the Ministry's directory names for the person, offered first in the journey (only while no department is chosen). */
  suggestedDepartment?: Dept | null;
  points: { total: number; week: number; month: number };
  level: { number: number; floor: number; next: number | null };
  streak: { current: number; longest: number; activeToday: boolean };
  rank: { week: number | null; month: number | null; all: number | null; department: number | null; departmentOf: number };
  badges: Array<{ key: string; earnedOn: string | null; progress: number; goal: number }>;
  files: { counted: number; approved: number; pending: number; rejected: number };
  /** Files sent since joining the waitlist that count towards opening ECONIC; at `goal` the person is admitted by themselves. */
  unlock?: { files: number; goal: number };
  questions: number;
  recent: Array<{ on: string; kind: "day" | "questions" | "file" | "approved" | "streak"; points: number }>;
  days: Array<{ day: string; points: number }>;
  today: string;
  rules: { activeDay: number; question: number; questionsPerDay: number; fileSent: number; fileApproved: number; filesToUnlock?: number; milestones: Array<{ days: number; bonus: number }> };
}

export interface PersonRow {
  rank: number; userId: string; name: string; nameAr?: string | null; title?: string | null; titleAr?: string | null; department: Dept | null;
  points: number; level: number; streak: number; files: number; approved: number; waitlist: boolean; me: boolean;
}
export interface DepartmentRow {
  rank: number; department: Dept; points: number; people: number; files: number; approved: number; lead: { userId: string; name: string; nameAr?: string | null }; mine: boolean;
}
export interface Board<R> { period: Period; from: string | null; to: string; total: number; rows: R[]; me: R | null }
export interface WaitlistRow { position: number; userId: string; name: string; nameAr?: string | null; department: Dept | null; points: number; files: number; approved: number; level: number; joinedAt: string | null; me: boolean }
export interface Waitlist { total: number; rows: WaitlistRow[]; me: WaitlistRow | null }

export type ContributionState = "processing" | "retry" | "in_review" | "approved" | "returned" | "rejected" | "duplicate" | "held" | "refused" | "failed";
export interface Contribution { id: string; fileName: string | null; size: number; at: string; decidedAt: string | null; state: ContributionState; points: number; department: Dept }
export interface Contributions { items: Contribution[]; totals: EngagementMe["files"] | null; portal: string | null; dailyLimit: number }

export interface AdminPerson {
  userId: string; state: "waitlist" | "admitted"; position: number | null; joinedAt: string | null; admittedAt: string | null; admittedBy: string | null;
  department: Dept | null; departmentClaimed: boolean; directoryDepartment: string | null; memberships: Dept[];
  points: { total: number; week: number; month: number }; level: number; streak: { current: number; longest: number };
  files: EngagementMe["files"]; unlock?: EngagementMe["unlock"]; questions: number; lastActiveAt: string | null;
}
export interface AdminPeople { today: string; waitlist: number; rows: AdminPerson[] }

const BASE = "/api/econic/engagement";
export const ENGAGEMENT_KEY = ["econic", "engagement"] as const;

/** The person's own standing; `null` when this server has no engagement yet (404), so nothing is gated on an older stack. */
export function useEngagementMe(options: { poll?: boolean } = {}) {
  const api = useApi();
  const { user, controller, updateUser } = useAuth();
  const qc = useQueryClient();
  const queryKey = [...ENGAGEMENT_KEY, "me", user?.id ?? null] as const;
  return useQuery({
    queryKey,
    queryFn: () => admissionReadyStanding({
      wasWaiting: qc.getQueryData<EngagementMe | null>(queryKey)?.state === "waitlist" || rememberedAccessState() === "waitlist",
      readSession: () => controller.read(),
      readStanding: async () => {
        try { return await api.get<EngagementMe>(`${BASE}/me`); }
        catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return null; throw e; }
      },
      readUser: () => api.get<AuthUser>("/api/auth/me"),
      updateUser,
    }),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: (query) => engagementPollInterval(query.state.data?.state, !!options.poll),
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function useLeaderboard(board: "people", period: Period): ReturnType<typeof useQuery<Board<PersonRow>>>;
export function useLeaderboard(board: "departments", period: Period): ReturnType<typeof useQuery<Board<DepartmentRow>>>;
export function useLeaderboard(board: "people" | "departments", period: Period) {
  const api = useApi();
  return useQuery({
    queryKey: [...ENGAGEMENT_KEY, "board", board, period],
    queryFn: () => api.get<Board<PersonRow> | Board<DepartmentRow>>(`${BASE}/leaderboard`, { period, board }),
    staleTime: 30_000,
  });
}

export function useWaitlist() {
  const api = useApi();
  return useQuery({ queryKey: [...ENGAGEMENT_KEY, "waitlist"], queryFn: () => api.get<Waitlist>(`${BASE}/waitlist`), staleTime: 30_000 });
}

export function useEngagementDepartments() {
  const api = useApi();
  return useQuery({
    queryKey: [...ENGAGEMENT_KEY, "departments"],
    queryFn: () => api.get<Unit[]>(`${BASE}/departments`),
    staleTime: 30_000,
  });
}

export function useContributions(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...ENGAGEMENT_KEY, "contributions"],
    queryFn: () => api.get<Contributions>(`${BASE}/contributions`),
    enabled,
    staleTime: 10_000,
    // While a file is still in the pipeline, look again shortly.
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.state === "processing") ? 4_000 : false),
  });
}

/** The welcome journey is done (or skipped): the waiting room opens from now on. */
export function useMarkOnboarded() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>(`${BASE}/me/onboarded`, {}),
    onSettled: () => void qc.invalidateQueries({ queryKey: [...ENGAGEMENT_KEY, "me"] }),
  });
}

export function useRetryContribution() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`${BASE}/contributions/${encodeURIComponent(id)}/retry`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...ENGAGEMENT_KEY, "contributions"] }),
  });
}

export function useChooseDepartment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (departmentKey: string) => api.put<void>(`${BASE}/me/department`, { departmentKey }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ENGAGEMENT_KEY }),
  });
}

export interface SendProgress { name: string; done: number; total: number }

/**
 * Sends files one after the other into the person's department: create → 5 MiB parts (raw PUTs with the bearer, three tries, one refresh on
 * 401) → complete. Returns what each file came to; the list and the standings refresh after each. A file over the Knowledge Bank's 200 MB
 * (MAX_UPLOAD_BYTES, the server's own ceiling) is refused here, before any part travels (3 Nov 2026).
 */
export function useSendFiles() {
  const api = useApi();
  const auth = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tokenRef = useRef(auth.token);
  tokenRef.current = auth.token;
  const ctl = auth.controller as unknown as { freshToken?: () => Promise<string | null> } | undefined;
  const token = useCallback(async () => (ctl?.freshToken ? (await ctl.freshToken()) ?? tokenRef.current : tokenRef.current), [ctl]);

  const putPart = useCallback(async (id: string, n: number, blob: Blob) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const res = await fetch(`${api.baseUrl}${BASE}/contributions/${id}/parts/${n}`, {
        method: "PUT", headers: { Authorization: `Bearer ${await token()}`, "Content-Type": "application/octet-stream" }, body: blob,
      });
      if (res.ok) return;
      if (res.status === 401 && attempt === 0) { await auth.refresh(); continue; }
      let problem: { title?: string; code?: string } | undefined;
      try { problem = await res.json(); } catch { /* none */ }
      throw new ApiError(res.status, problem?.title ?? `${res.status}`, problem as never);
    }
  }, [api, token, auth]);

  return useCallback(async (files: File[], onProgress: (p: SendProgress) => void) => {
    const results: Array<{ name: string; ok: boolean; error?: string; code?: string }> = [];
    for (const file of files) {
      if (file.size > MAX_UPLOAD_BYTES) {
        results.push({ name: file.name, ok: false, error: t("assistant:kb.upload.tooLarge"), code: "kb_too_large" });
        continue;
      }
      try {
        const created = await api.post<{ uploadId: string; partSize: number; parts: number }>(`${BASE}/contributions`, {
          fileName: file.name, size: file.size, contentType: file.type || "application/octet-stream",
        });
        const parts = Math.max(1, created.parts);
        for (let n = 1; n <= parts; n += 1) {
          const from = (n - 1) * created.partSize;
          await putPart(created.uploadId, n, file.slice(from, Math.min(file.size, from + created.partSize)));
          onProgress({ name: file.name, done: n, total: parts });
        }
        await api.post(`${BASE}/contributions/${created.uploadId}/complete`);
        results.push({ name: file.name, ok: true });
      } catch (e) {
        const problem = e instanceof ApiError ? (e.problem as { title?: string; code?: string } | undefined) : undefined;
        results.push({ name: file.name, ok: false, error: problem?.title ?? (e instanceof Error ? e.message : String(e)), code: problem?.code });
      }
      void qc.invalidateQueries({ queryKey: ENGAGEMENT_KEY });
    }
    return results;
  }, [api, putPart, qc, t]);
}

// ------------------------------------------------------------------ administration

export function useAdminPeople(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...ENGAGEMENT_KEY, "admin"],
    queryFn: async () => {
      try { return await api.get<AdminPeople>(`${BASE}/admin/people`); }
      catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return null; throw e; }
    },
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useAdmission() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ENGAGEMENT_KEY });
    void qc.invalidateQueries({ queryKey: ["econic", "admin", "accounts"] });
  };
  return {
    admit: useMutation({ mutationFn: (v: { id: string; confirmDepartment: boolean }) => api.post<void>(`${BASE}/admin/people/${encodeURIComponent(v.id)}/admit`, { confirmDepartment: v.confirmDepartment }), onSuccess: refresh }),
    waitlist: useMutation({ mutationFn: (id: string) => api.post<void>(`${BASE}/admin/people/${encodeURIComponent(id)}/waitlist`, {}), onSuccess: refresh }),
    admitNext: useMutation({ mutationFn: (v: { count: number; confirmDepartment: boolean }) => api.post<{ admitted: string[] }>(`${BASE}/admin/admit-next`, v), onSuccess: refresh }),
  };
}

/** A department's name in the reader's language. */
export const deptName = (d: Dept | null | undefined, locale: string) => (d ? (locale === "ar" && d.nameAr ? d.nameAr : d.name) : null);
/** A person's name in the reader's language. */
export const personName = (p: { name: string; nameAr?: string | null }, locale: string) => (locale === "ar" && p.nameAr ? p.nameAr : p.name);
